import { useMonarch } from "@redventures/cohesion-utils-react";
import { useMemo } from "react";
import { ProactiveModalContent } from "~/types/chatbot";
import { mapUiConfigToNested, mapStylesArrayToObject } from "./helpers/chatbotHelpers";

type ChatType = "Giga" | "Proactive" | "ProactiveV2";

interface IdleTriggerConfig {
  timeout: number; // in seconds
  proactiveModalContent?: ProactiveModalContent;
}

interface ThresholdTriggerConfig {
  threshold: number; // number of actions before trigger fires
  proactiveModalContent?: ProactiveModalContent;
}

interface FormErrorTriggerConfig {
  forceModal?: boolean; // if true, always show proactive modal for form-error triggers (Giga and Proactive)
  proactiveModalContent?: ProactiveModalContent;
}

interface BackButtonTriggerConfig {
  enabled?: boolean; // if false, back button trigger will be disabled for both Giga and Proactive chat
  proactiveModalContent?: ProactiveModalContent;
}

interface ChatbotTriggers {
  idle?: IdleTriggerConfig;
  vasUncertainty?: ThresholdTriggerConfig;
  productUncertainty?: ThresholdTriggerConfig;
  formError?: FormErrorTriggerConfig;
  backButton?: BackButtonTriggerConfig;
}

interface PositionConfig {
  bottom?: string; // Distance from bottom (e.g., "50px")
  right?: string; // Distance from right (e.g., "40px")
}

interface ProactiveConfig {
  message?: string; // Proactive message text (e.g., "Need Help?")
  type?: "default" | "side_rail"; // Proactive display type
  display?: "horizontal" | "vertical"; // Side rail orientation (required when type is "side_rail")
}

interface AnimateConfig {
  message: string; // Animation message text (e.g., "Need help?")
}

interface IconAndStatusIndicatorStyles {
  backgroundColor?: string;
  color?: string;
  borderRadius?: string;
  boxShadow?: string;
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  left?: string;
  bottom?: string;
  top?: string;
  padding?: string;
  margin?: string;
  border?: string;
  borderColor?: string;
  borderWidth?: string;
  opacity?: string;
  transform?: string;
  transition?: string;
  zIndex?: string;
  enable?: boolean;
}

interface UiConfig {
  chatBubbleStyles?: IconAndStatusIndicatorStyles;
  radioButtonStyles?: IconAndStatusIndicatorStyles;
  gigaIconStyles?: IconAndStatusIndicatorStyles;
  chatBubbleTailwindStyles?: IconAndStatusIndicatorStyles;
  messageStyles?: {
    color?: string;
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: string;
  };
}

export interface UseChatbotMonarchReturnType {
  chatType?: ChatType;
  isLoading: boolean;
  sdkVersion: string;
  triggers?: ChatbotTriggers;
  position?: PositionConfig;
  proactive?: ProactiveConfig;
  animate?: AnimateConfig;
  triggerType?: "proactive"; // SDK trigger type
  proactiveModalContent?: ProactiveModalContent | undefined;
  pageType?: string;
  uiConfig?: UiConfig;
}

/** Raw uiConfigMessage from Monarch: array of { name, value } → becomes messageStyles */
interface RawUiConfigMessageItem {
  name: string;
  value: string;
}

interface CartChatbotNewRuleGiga {
  sdkVersion: string;
  chatType: ChatType;
  triggers?: ChatbotTriggers;
  position?: PositionConfig;
  proactive?: ProactiveConfig;
  animate?: AnimateConfig;
  triggerType?: "proactive";
  proactiveModalContent?: ProactiveModalContent | undefined;
  pageType?: string;
  uiConfig?: UiConfig;
  uiConfigMessage?: RawUiConfigMessageItem[] | undefined;
}

const DEFAULT_FEATURE_FLAGS: CartChatbotNewRuleGiga = {
  chatType: undefined,
  sdkVersion: "CB-20251113.01",
  triggers: undefined,
  position: undefined,
  proactive: undefined,
  animate: undefined,
  triggerType: undefined,
  proactiveModalContent: undefined,
  pageType: undefined,
  uiConfig: undefined,
};

const useChatbotMonarch = (): UseChatbotMonarchReturnType => {
  const { result, isLoading, err } = useMonarch();

  const flags = useMemo(() => {
    // Wait until loading is complete before processing flags
    if (isLoading || err) {
      return {
        ...DEFAULT_FEATURE_FLAGS,
        isLoading: true,
      };
    }

    const value = result?.source?.cartChatbot?.value;

    // Component return: value.data. Native return: value itself.
    const fromComponent = (value as { data?: CartChatbotNewRuleGiga })?.data;
    const fromNative = value as CartChatbotNewRuleGiga | undefined;
    const ruleData = (fromComponent ?? fromNative ?? {}) as CartChatbotNewRuleGiga;

    const baseUiConfig = mapUiConfigToNested(ruleData.uiConfig) as UiConfig | undefined;
    const messageStyles = mapStylesArrayToObject(ruleData.uiConfigMessage) as UiConfig["messageStyles"];

    const uiConfig: UiConfig | undefined =
      baseUiConfig != null || messageStyles != null
        ? {
            ...(baseUiConfig ?? {}),
            ...(messageStyles != null && { messageStyles }),
          }
        : undefined;

    return {
      ...DEFAULT_FEATURE_FLAGS,
      ...ruleData,
      ...(!!uiConfig && { uiConfig }),
      isLoading: false,
    } as UseChatbotMonarchReturnType;
  }, [result, isLoading]);

  return flags;
};

export default useChatbotMonarch;
